/* eslint-disable camelcase */
import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import Box from '@components/Atoms/Box';
import ShowIf from '@components/Atoms/ShowIf';
import { Canvas } from 'canvas-embed';
import InformationCard from '@components/Component/InformationCard';
import { Sync } from '@mui/icons-material';
import { startMeasurement, measureTimeBetween } from '@utils/dataDogActions/Helpers';
import { trackCanvasLoaded } from '@utils/dataDogActions/canvasEmbed';
import { useUser } from '@reducers/user/hooks';

const CHALKTALK_CANVAS_HOST = 'https://canvasapp.chalktalk.com';
const CANVAS_HOST_MATCH = 'canvasapp.chalktalk.com';

interface CanvasEmbedProps {
  canvas_id: string;
  canvas_auth_token: string;
}

const CanvasEmbed: React.FC<CanvasEmbedProps> = ({ canvas_id, canvas_auth_token }) => {
  const user = useUser();
  const [fetchPatched, setFetchPatched] = useState(false);
  const [isReady, setIsReady] = useState(false);
  // tracks which filter combo have already been RUM tracked
  // use searchParams url as identifier
  const loadedFilterRef = useRef<string[]>([]);

  const trackCanvasLoadTime = useCallback(
    (url: RequestInfo | URL, startedAt: number, status: number) => {
      const urlObject = new URL(url instanceof Request ? url.url : url.toString());
      const filterSignature = Object.fromEntries(urlObject.searchParams.entries());
      const filterSignatureString = JSON.stringify(filterSignature);
      const timeTaken = measureTimeBetween(startedAt);

      if (loadedFilterRef.current.includes(filterSignatureString)) {
        return;
      }
      loadedFilterRef.current.push(filterSignatureString);

      trackCanvasLoaded({
        status,
        userId: user.id,
        canvasId: canvas_id,
        value: timeTaken,
        filterSignature,
      });
    },
    [canvas_id, user.id],
  );

  useLayoutEffect(() => {
    if (!user.id) {
      return;
    }
    const originalFetch = window.fetch;

    // Intercept Canvas API calls to:
    //  - drive the loading spinner (isReady)
    //  - emit Datadog timing actions for each Canvas API fetch
    window.fetch = (async (url: RequestInfo | URL, init?: RequestInit) => {
      const urlString = url instanceof Request ? url.url : String(url);
      if (!urlString.includes(CANVAS_HOST_MATCH)) {
        return originalFetch(url, init);
      }

      setIsReady(false);
      const startedAt = startMeasurement();

      try {
        const response = await originalFetch(url, init);
        trackCanvasLoadTime(url, startedAt, response.status);
        return response;
      } catch (error) {
        trackCanvasLoadTime(url, startedAt, error.status);
        throw error;
      } finally {
        setIsReady(true);
      }
    }) as typeof window.fetch;

    setFetchPatched(true);

    return () => {
      window.fetch = originalFetch;
      setFetchPatched(false);
    };
  }, [canvas_id, trackCanvasLoadTime, user.id]);

  return (
    <>
      <ShowIf If={!isReady}>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '1rem',
            '& p': {
              maxWidth: '100%',
            },
          }}
        >
          <InformationCard
            img="/images/report-generator.png"
            title="Aggregating Data..."
            description={
              <>
                Please keep this page open while we prepare your report.
                <br />
                <Sync sx={{ animation: 'spin 2s linear infinite reverse', mt: '1rem' }} />
              </>
            }
            extraStyle={{ width: '100%', height: '40vh' }}
          />
        </Box>
      </ShowIf>
      <Box
        sx={{
          visibility: isReady ? 'visible' : 'hidden',
          position: isReady ? 'relative' : 'absolute',
          width: '100%',
        }}
      >
        <ShowIf If={fetchPatched}>
          <Canvas
            canvasId={canvas_id}
            authToken={canvas_auth_token}
            host={CHALKTALK_CANVAS_HOST}
          />
        </ShowIf>
      </Box>
    </>
  );
};

export default CanvasEmbed;
